<template>
  <div class="container-fluid">
    <div class="block-header position-sticky">
      <bread-crumb :breadCrumbIteams="breadCrumbIteams"></bread-crumb>
    </div>
    <div class="row clearfix">
      <div class="col-md-12">
        <!-- Receiver-level summary (context first, then the listing) -->
        <div v-if="receiverEvidence" class="row lr-summary">
          <div class="col-sm-6 col-lg-3 mb-3">
            <div
              class="lr-metric"
              :class="'lr-border-' + receiverEvidence.level.toLowerCase()"
            >
              <div class="lr-metric-label">RISK SCORE</div>
              <div class="lr-metric-value">{{ receiverEvidence.score }}</div>
              <div class="lr-metric-sub">{{ receiverEvidence.level }} link risk</div>
            </div>
          </div>
          <div class="col-sm-6 col-lg-3 mb-3">
            <div class="lr-metric lr-border-blue">
              <div class="lr-metric-label">LINKED REMITTERS</div>
              <div class="lr-metric-value">{{ receiverEvidence.linkedRemitters }}</div>
              <div class="lr-metric-sub">Genuine senders to this receiver</div>
            </div>
          </div>
          <div class="col-sm-6 col-lg-3 mb-3">
            <div class="lr-metric lr-border-amber">
              <div class="lr-metric-label">SHARED IDENTITIES</div>
              <div class="lr-metric-value">{{ receiverEvidence.customerMatch }}</div>
              <div class="lr-metric-sub">Other records on the same instrument</div>
            </div>
          </div>
          <div class="col-sm-6 col-lg-3 mb-3">
            <div class="lr-metric lr-border-green">
              <div class="lr-metric-label">RECEIVER SOURCE</div>
              <div class="lr-metric-value">BEN</div>
              <div class="lr-metric-sub">Beneficiary table enforced</div>
            </div>
          </div>
        </div>
        <div class="card">
          <div
            class="header border-bottom d-flex align-items-start justify-content-between flex-wrap"
          >
            <div class="pr-3">
              <h5 class="text-primary font-weight-normal mb-0">
                Linked Remitters
              </h5>
              <p class="text-muted small mb-0 mt-1">
                Genuine registered customers who added
                <strong v-if="receiverName">{{ receiverName }}</strong>
                <template v-else>this receiver</template>
                as a beneficiary and send to them. The receiver's own record and
                unverified placeholder records are excluded.
              </p>
            </div>
            <span
              v-if="linkedSenders && linkedSenders.length"
              class="badge lr-count-pill mt-1"
            >
              {{ totalRows }} linked remitter{{ totalRows === 1 ? "" : "s" }}
            </span>
          </div>
          <div class="body">
            <b-table
              table-class="table-custom border"
              head-variant="light"
              :items="
                typeof linkedSenders === 'array' ||
                typeof linkedSenders === 'object'
                  ? linkedSenders
                  : []
              "
              :fields="linkedSendersfields"
              :per-page="perPage"
              :sort-by.sync="sortBy"
              :sort-desc.sync="sortDesc"
              @sort-changed="onSortChanged"
              :emptyText="'No linked remitters found for this receiver.'"
              show-empty
              responsive
              striped
              hover
            >
              <template #head()="{ label, field: { key } }">
                {{ label }}
                <!-- Custom icons -->
                <template>
                  <i v-if="sortBy !== key" class=""></i>
                  <i
                    v-else-if="sortDesc"
                    class="fa fa-long-arrow-down ml-1"
                  ></i>
                  <i v-else class="fa fa-long-arrow-up ml-1"></i>
                </template>
              </template>
              <template #cell(country)="data">
                <div class="align-items-center d-flex">
                  <span
                    class="ml-0"
                    v-if="data.value.isocode"
                    :class="`flag-icon flag-icon-${data.value.isocode} fi-lg fi-round`"
                  ></span>
                  <span
                    class="ml-2"
                    v-b-tooltip.hover
                    :title="data.item.country?.name"
                  >
                    {{ data.item.country?.isocode?.toUpperCase() }}
                  </span>
                </div>
              </template>
              <template #cell(name)="data">
                <a
                  href="javascript:void(0);"
                  @click="editCustomer(data, 'sender')"
                >
                  {{
                    data.item.name
                      ? data.item.name +
                        " " +
                        data.item.mname +
                        " " +
                        data.item.lname
                      : "-"
                  }}
                </a>
              </template>
              <template #cell(receiver_name)="data">
                <a
                  href="javascript:void(0);"
                  @click="editCustomer(data, 'receiver')"
                >
                  {{
                    data.item.receiver_fname
                      ? data.item.receiver_fname +
                        " " +
                        data.item.receiver_lname
                      : "-"
                  }}
                </a>
              </template>
              <template #cell(payangel_id)="data">
                <span>
                  {{ data.value }}
                  <i
                    class="fa fa-copy"
                    v-if="data.value != '-'"
                    @click="copyText(data.value, 'Payangel Id')"
                  ></i>
                </span>
              </template>
              <template #cell(phonenumber)="data">
                <span>
                  {{ data.value }}
                  <i
                    class="fa fa-copy"
                    v-if="data.value != '-'"
                    @click="copyText(data.value, 'Phone Number')"
                  ></i>
                </span>
              </template>
              <template #cell(status)="data">
                <p class="function-badge badge-group-table mb-0">
                  <span class="badge ml-0" :class="data.value.class">
                    {{ data.value.name }}
                  </span>
                </p>
              </template>
              <template #cell(link_signal)="data">
                <div v-if="signalFor(data.item.id)">
                  <span
                    class="badge risk-pill"
                    :class="'risk-' + signalFor(data.item.id).level.toLowerCase()"
                  >
                    {{ signalFor(data.item.id).level }}
                  </span>
                  <div class="lr-reasons mt-1">
                    <span
                      v-for="(r, i) in signalFor(data.item.id).reasons"
                      :key="i"
                      class="lr-reason-chip"
                      :class="reasonChipClass(signalFor(data.item.id).level)"
                    >
                      {{ r }}
                    </span>
                  </div>
                </div>
                <span v-else class="text-muted small">—</span>
              </template>
              <template #cell(action)="data">
                <b-dropdown
                  id="dropdown-1"
                  text="Actions"
                  size="sm"
                  right
                  variant="outline-primary"
                >
                  <template v-if="$store.getters['authpermission/hasAnyPermission'](['receiver.edit'])">
                    <b-dropdown-item @click="showRelation(data)">
                      Edit
                    </b-dropdown-item>
                  </template>
                </b-dropdown>
              </template>
            </b-table>
            <!-- Table Pagination -->
            <div
              class="d-flex py-3 align-items-center justify-content-between table-pagination"
            >
              <div class="per-page">
                <b-form-group
                  label="Per page"
                  label-for="per-page-select"
                  class="mb-0"
                >
                  <b-form-select
                    id="per-page-select"
                    v-model="perPage"
                    :options="pageOptions"
                    size="sm"
                  ></b-form-select>
                </b-form-group>
              </div>
              <div class="number-page">
                <b-pagination
                  v-model="currentPage"
                  :total-rows="totalRows"
                  first-text="First"
                  last-text="Last"
                  :per-page="perPage"
                  align="right"
                ></b-pagination>
              </div>
              <div class="go-page">
                <div class="input-group">
                  <div class="input-group-prepend">
                    <span class="input-group-text">Go To Page</span>
                  </div>
                  <input
                    type="text"
                    v-model="currentPageJump"
                    class="form-control"
                    value=""
                    @keyup.enter="onEnter"
                    onkeypress="return event.charCode >= 48 && event.charCode <= 57"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Relationship snapshot for investigation (derived from real linked-risk data) -->
        <div v-if="receiverEvidence" class="card">
          <div class="header border-bottom lr-snapshot-head">
            <h6 class="mb-0 font-weight-bold">
              Relationship snapshot for investigation
            </h6>
          </div>
          <div class="body">
            <div class="row">
              <div class="col-md-3 mb-2">
                <div class="lr-snap-title">REMITTERS</div>
                <div
                  v-for="(s, i) in linkedSenders"
                  :key="i"
                  class="lr-snap-card"
                  :class="snapBorder(signalFor(s.id))"
                >
                  <div class="lr-snap-name">
                    {{ (s.name + " " + s.lname).trim() || "—" }}
                  </div>
                  <div class="lr-snap-sub">
                    {{ s.payangel_id }} ·
                    {{ s.country && s.country.isocode ? s.country.isocode.toUpperCase() : "—" }}
                  </div>
                </div>
              </div>
              <div class="col-md-3 mb-2">
                <div class="lr-snap-title">BENEFICIARY RECORD</div>
                <div class="lr-snap-card lr-border-green">
                  <div class="lr-snap-name">{{ receiverEvidence.receiverName }}</div>
                  <div class="lr-snap-sub">
                    {{ linkedSenders.length }} linked record{{ linkedSenders.length === 1 ? "" : "s" }} — Beneficiary sourced
                  </div>
                </div>
              </div>
              <div class="col-md-3 mb-2">
                <div class="lr-snap-title">CUSTOMER RELATIONSHIP</div>
                <div class="lr-snap-card lr-border-amber">
                  <div class="lr-snap-name">
                    {{ receiverEvidence.customerMatch }} shared identit{{ receiverEvidence.customerMatch === 1 ? "y" : "ies" }}
                  </div>
                  <div class="lr-snap-sub">Resolved by mobile or bank match</div>
                </div>
                <div
                  v-if="receiverEvidence.indicators && receiverEvidence.indicators.indexOf('NAME VARIANT') !== -1"
                  class="lr-snap-card lr-border-amber"
                >
                  <div class="lr-snap-name">Name variance</div>
                  <div class="lr-snap-sub">
                    A verified customer resolves under a different name
                  </div>
                </div>
              </div>
              <div class="col-md-3 mb-2">
                <div class="lr-snap-title">INSTRUMENTS</div>
                <template
                  v-if="receiverEvidence.sharedIdentityGroups && receiverEvidence.sharedIdentityGroups.length"
                >
                  <div
                    v-for="(g, i) in receiverEvidence.sharedIdentityGroups"
                    :key="i"
                    class="lr-snap-card lr-border-red"
                  >
                    <div class="lr-snap-name">{{ g.label }} {{ g.value }}</div>
                    <div class="lr-snap-sub">
                      On {{ g.identityCount }} other record{{ g.identityCount === 1 ? "" : "s" }}
                    </div>
                  </div>
                </template>
                <div v-else class="lr-snap-card lr-border-green">
                  <div class="lr-snap-name">No reused instruments</div>
                  <div class="lr-snap-sub">
                    Payout details not shared across identities
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <!-- <div class="card">
                    <div class="header border-bottom">
                        <h5 class="text-primary font-weight-normal mb-0">Block Box</h5>
                    </div>
                    <div class="body">

                    </div>
                </div> -->
        <div class="row">
          <div class="col-lg-6 mb-3">
            <div class="card h-100 mb-0">
              <div
                class="header border-bottom accordian-toggle d-flex justify-content-between align-items-center"
                v-b-toggle.bankAccounts
                @click="getBankAccounts"
              >
                <h5 class="text-primary font-weight-normal mb-0">
                  Bank Accounts
                </h5>
                <i class="fa fa-chevron-down text-muted"></i>
              </div>
              <b-collapse id="bankAccounts" visible>
                <div class="body">
                  <b-table
                    responsive
                    outlined
                    table-class="mb-0 table-custom"
                    head-variant="light"
                    :items="banksaccounts"
                    :fields="bankColumns"
                    :emptyText="'No bank accounts found.'"
                    show-empty
                    striped
                  >
                    <template #cell(account_number)="data">
                      <a
                        href="javascript:void(0);"
                        @click="viewAccounts(data.item)"
                      >
                        {{ data.value }}
                      </a>
                    </template>
                    <template #cell(block_status)="data">
                      <span
                        class="badge"
                        :class="
                          data.item.is_blocked
                            ? 'badge-danger'
                            : 'badge-success'
                        "
                      >
                        {{ data.item.is_blocked ? "Blocked" : "Active" }}
                      </span>
                      <div
                        v-if="data.item.is_blocked && data.item.blocked_reason"
                        class="text-muted small mt-1"
                        v-b-tooltip.hover
                        :title="data.item.blocked_reason"
                      >
                        {{ data.item.blocked_reason }}
                      </div>
                    </template>
                    <template #cell(block_action)="data">
                      <b-button
                        v-if="!data.item.is_blocked"
                        size="sm"
                        variant="outline-danger"
                        @click="toggleBankBlock(data.item, true)"
                      >
                        Block
                      </b-button>
                      <b-button
                        v-else
                        size="sm"
                        variant="outline-success"
                        @click="toggleBankBlock(data.item, false)"
                      >
                        Unblock
                      </b-button>
                    </template>
                  </b-table>
                </div>
              </b-collapse>
            </div>
          </div>
          <div class="col-lg-6 mb-3">
            <div class="card h-100 mb-0">
              <div
                class="header border-bottom accordian-toggle d-flex justify-content-between align-items-center"
                v-b-toggle.momoAccounts
                @click="getmomoAccounts"
              >
                <h5 class="text-primary font-weight-normal mb-0">
                  MOMO Accounts
                </h5>
                <i class="fa fa-chevron-down text-muted"></i>
              </div>
              <b-collapse id="momoAccounts" visible>
                <div class="body">
                  <b-table
                    responsive
                    outlined
                    table-class="mb-0 table-custom"
                    head-variant="light"
                    :items="momoaccounts"
                    :fields="momoColumns"
                    :emptyText="'No mobile money accounts found.'"
                    show-empty
                    striped
                  >
                    <template #cell(momo_number)="data">
                      <a
                        href="javascript:void(0);"
                        @click="viewAccounts(data.item)"
                      >
                        {{ data.value }}
                      </a>
                    </template>
                    <template #cell(block_status)="data">
                      <span
                        class="badge"
                        :class="
                          data.item.is_blocked
                            ? 'badge-danger'
                            : 'badge-success'
                        "
                      >
                        {{ data.item.is_blocked ? "Blocked" : "Active" }}
                      </span>
                      <div
                        v-if="data.item.is_blocked && data.item.blocked_reason"
                        class="text-muted small mt-1"
                        v-b-tooltip.hover
                        :title="data.item.blocked_reason"
                      >
                        {{ data.item.blocked_reason }}
                      </div>
                    </template>
                    <template #cell(block_action)="data">
                      <b-button
                        v-if="!data.item.is_blocked"
                        size="sm"
                        variant="outline-danger"
                        @click="toggleMomoBlock(data.item, true)"
                      >
                        Block
                      </b-button>
                      <b-button
                        v-else
                        size="sm"
                        variant="outline-success"
                        @click="toggleMomoBlock(data.item, false)"
                      >
                        Unblock
                      </b-button>
                    </template>
                  </b-table>
                </div>
              </b-collapse>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
<script>
import {
  GET_LINKED_REMITTERS,
  GET_BANKACCOUNTS,
  GET_MOMOACCOUNTS,
  BLOCK_RECEIVER_MOMO,
  UNBLOCK_RECEIVER_MOMO,
  BLOCK_RECEIVER_BANK,
  UNBLOCK_RECEIVER_BANK,
  GET_RECEIVER_RISK_EVIDENCE,
  GET_RECEIVER_REMITTER_SIGNALS,
} from "../../../store/actions.type.js";
import { mapGetters } from "vuex";
export default {
  name: "LinkedRemitterPage",
  components: {
    BreadCrumb: () => import("@/components/BreadCrumb.vue"),
  },
  props: {
    beneficiaryDetail: Object,
  },
  data() {
    return {
      linkedSenders: [],
      remitterSignals: {},
      receiverEvidence: null,
      linkedSendersfields: [
        { key: "country", tdClass: "p-2" },
        { key: "name", sortable: true, tdClass: "p-2" },
        { key: "receiver_name", label: "Receiver Name", tdClass: "p-2" },
        { key: "payangel_id", tdClass: "p-2" },
        { key: "phonenumber", tdClass: "p-2" },
        { key: "status", tdClass: "p-2" },
        { key: "link_signal", label: "Link Signal", tdClass: "p-2" },
        { key: "action", tdClass: "p-2" },
      ],
      breadCrumbIteams: [
        {
          title: "Receivers",
          links: "",
        },
        {
          title: "Receivers Details", //Linked remitters
          links: "",
        },
      ],
      banksaccounts: [],
      momoaccounts: [],
      bankAccountsloaded: false,
      momoAccountsloaded: false,
      bankfields: [
        { key: "name", label: "Name", thClass: "th_sortfix" },
        {
          key: "account_number",
          label: "Account Number",
          thClass: "th_sortfix",
        },
        { key: "block_status", label: "Status", thClass: "th_sortfix" },
        {
          key: "block_action",
          label: "Action",
          thClass: "th_sortfix text-right pr-4",
          tdClass: "text-right",
        },
      ],
      momoaccountfields: [
        { key: "network", label: "Network", thClass: "th_sortfix" },
        { key: "momo_number", label: "Mobile Number", thClass: "th_sortfix" },
        { key: "block_status", label: "Status", thClass: "th_sortfix" },
        {
          key: "block_action",
          label: "Action",
          thClass: "th_sortfix text-right pr-4",
          tdClass: "text-right",
        },
      ],
      totalRows: 1,
      currentPage: 1,
      currentPageJump: 1,
      perPage: 10,
      pageOptions: [10, 15, 20],
      sortBy: "",
      sortDesc: true,
    };
  },
  methods: {
    editCustomer(data, user) {
      let id =
        user == "sender"
          ? data.item.id
          : this.$CryptoJS.AES.decrypt(
              this.$route.params?.beneficiary_id,
              "beneficiary_id",
            ).toString(this.$CryptoJS.enc.Utf8);
      this.$router.push({
        name: "customer-edit",
        params: {
          customer_id: this.$CryptoJS.AES.encrypt(id, "customer_id").toString(),
        },
      });
    },
    // view edit Accounts page
    viewAccounts(data) {
      let params = {};
      if (data.account_number) {
        params.name = "Bank Account";
        params.value = data.account_number;
      }
      if (data.momo_number) {
        params.name = "MOMO Account";
        params.value = data.momo_number;
      }
      this.$router.push({
        path: "/accounts/edit",
        query: {
          name: this.$CryptoJS.AES.encrypt(params.name, "name").toString(),
          value: this.$CryptoJS.AES.encrypt(params.value, "value").toString(),
        },
      });
    },
    // Get remitter bank accounts details
    getBankAccounts() {
      if (this.bankAccountsloaded) {
        return;
      }
      const id = this.$CryptoJS.AES.decrypt(
        this.$route.params?.beneficiary_id,
        "beneficiary_id",
      ).toString(this.$CryptoJS.enc.Utf8);
      const tempitems = [];
      let tempparam = `?beneficiaryId=${id}`;
      this.$store.dispatch(GET_BANKACCOUNTS, tempparam).then((response) => {
        if (response.status == 200) {
          let details = response.data.details;
          details.map((item) => {
            let accounts = {
              id: item.id,
              name: item.bank_name ? item.bank_name : "-",
              branchname: item.branchname ? item.branchname : "-",
              account_number: item.account_number ? item.account_number : "-",
              is_blocked: !!item.is_blocked,
              blocked_reason: item.blocked_reason || null,
            };
            tempitems.push(accounts);
          });
        } else {
          this.$toast.error(response.data.message, { position: "top-right" });
        }
        this.bankAccountsloaded = true;
        this.banksaccounts = tempitems;
      });
    },
    // Get remitter momo accounts details
    getmomoAccounts() {
      if (this.momoAccountsloaded) {
        return;
      }
      const id = this.$CryptoJS.AES.decrypt(
        this.$route.params?.beneficiary_id,
        "beneficiary_id",
      ).toString(this.$CryptoJS.enc.Utf8);
      const tempitems = [];
      let tempparam = `?beneficiaryId=${id}`;
      this.$store.dispatch(GET_MOMOACCOUNTS, tempparam).then((response) => {
        if (response.status == 200) {
          let details = response.data.details;
          details.map((item) => {
            let accounts = {
              id: item.id,
              network: item.provider_name,
              momo_number: item.wallet_number,
              is_blocked: !!item.is_blocked,
              blocked_reason: item.blocked_reason || null,
            };
            tempitems.push(accounts);
          });
        } else {
          this.$toast.error(response.data.message, { position: "top-right" });
        }
        this.momoAccountsloaded = true;
        this.momoaccounts = tempitems;
      });
    },
    // ── Block / unblock momo (L2) & bank (L3) ─────────────────────────
    blockConfirmDialog(text, confirmText, withReason) {
      return this.$swal
        .mixin({
          customClass: {
            confirmButton: "btn btn-primary btn-lg my-3",
            cancelButton: "btn btn-outline-secondary btn-lg",
          },
          buttonsStyling: false,
        })
        .fire({
          title: "Are you sure?",
          text,
          icon: "warning",
          showCancelButton: true,
          confirmButtonText: confirmText,
          ...(withReason
            ? { input: "text", inputLabel: "Reason (optional)" }
            : {}),
        });
    },
    blockIsSuccess(response) {
      return response && (response.status === 200 || response.status === 201);
    },
    async toggleMomoBlock(item, block) {
      const result = await this.blockConfirmDialog(
        block
          ? "Block this mobile money number? This sender will not be able to send money to this receiver's number."
          : "Unblock this mobile money number?",
        block ? "Yes, block" : "Yes, unblock",
        block,
      );
      if (!result.isConfirmed) return;
      const action = block ? BLOCK_RECEIVER_MOMO : UNBLOCK_RECEIVER_MOMO;
      const response = await this.$store.dispatch(action, {
        id: item.id,
        reason: result.value || undefined,
      });
      if (this.blockIsSuccess(response)) {
        this.$toast.success(response.data.message, { position: "top-right" });
        this.momoAccountsloaded = false;
        this.getmomoAccounts();
      } else {
        this.$toast.error(
          (response && response.data && response.data.message) ||
            "Operation failed",
          { position: "top-right" },
        );
      }
    },
    async toggleBankBlock(item, block) {
      const result = await this.blockConfirmDialog(
        block
          ? "Block this bank account? This sender will not be able to send money to this receiver's account."
          : "Unblock this bank account?",
        block ? "Yes, block" : "Yes, unblock",
        block,
      );
      if (!result.isConfirmed) return;
      const action = block ? BLOCK_RECEIVER_BANK : UNBLOCK_RECEIVER_BANK;
      const response = await this.$store.dispatch(action, {
        id: item.id,
        reason: result.value || undefined,
      });
      if (this.blockIsSuccess(response)) {
        this.$toast.success(response.data.message, { position: "top-right" });
        this.bankAccountsloaded = false;
        this.getBankAccounts();
      } else {
        this.$toast.error(
          (response && response.data && response.data.message) ||
            "Operation failed",
          { position: "top-right" },
        );
      }
    },
    empty() {
      this.totalRows = 1;
      this.currentPage = 1;
      this.currentPageJump = 1;
      this.perPage = 10;
      this.sortBy = "";
      this.sortDesc = true;
    },
    onSortChanged(ctx) {
      this.sortBy = ctx.sortBy;
      this.sortDesc = ctx.sortDesc;
      this.items = [];
      if (this.sortBy != "") {
        this.linkedRemitter();
      }
    },
    onEnter(e) {
      var paginationMenu = Math.ceil(this.totalRows / this.perPage);
      if (e.target.value > 0) {
        if (paginationMenu >= e.target.value) {
          this.currentPage = this.currentPageJump = e.target.value;
        } else {
          this.currentPage = this.currentPageJump = paginationMenu;
        }
      } else {
        this.currentPage = this.currentPageJump = 1;
      }
    },
    copyText(data, type) {
      var m = document;
      data = m.createTextNode(data);
      var w = window;
      var b = m.body;
      var d = {};
      b.appendChild(data);
      d = m.createRange();
      var g = w.getSelection;
      d.selectNodeContents(data);
      g().removeAllRanges();
      g().addRange(d);
      m.execCommand("copy");
      g().removeAllRanges();
      data.remove();
      this.$toast.success(`${type} Copied`, { position: "top-right" });
    },
    blacklistIp() {
      const swalWithBootstrapButtons = this.$swal.mixin({
        customClass: {
          confirmButton: "btn btn-primary btn-lg my-3",
          cancelButton: "btn btn-outline-secondary btn-lg",
        },
        buttonsStyling: false,
      });
      swalWithBootstrapButtons
        .fire({
          title: "Are you sure?",
          text: "You want to blacklist Ip for this Customer?",
          icon: "warning",
          showCancelButton: true,
          confirmButtonText: "Yes, blacklist it!",
        })
        .then((result) => {
          if (result.isConfirmed) {
            swalWithBootstrapButtons.fire(
              "Blacklisted!",
              "IP address has been blacklisted for this Customer.",
              "success",
            );
          }
        });
    },
    showRelation(data) {
      let params = {
        beneficiary_id: this.$route.params?.beneficiary_id,
        customer_id: this.$CryptoJS.AES.encrypt(
          data.item.id,
          "customer_id",
        ).toString(),
        country_id: this.$route.params?.country_id,
      };
      this.$router.push({ name: "beneficiary-edit", params: params });
    },
    async linkedRemitter() {
      const tempitems = [];
      this.linkedSenders = [];
      let page = "";
      let page_size = "";
      let sortby = "";
      let sort = false;
      if (this.currentPage != undefined) {
        page = this.currentPage;
      } else {
        page = 1;
      }
      if (this.perPage != undefined) {
        page_size = this.perPage;
      } else {
        page_size = 50;
      }
      if (this.sortBy == "") {
        sortby = "createdAt";
      } else if (this.sortBy == "name") {
        sortby = "firstname";
      } else {
        sortby = this.sortBy;
      }
      if (this.sortDesc == true) {
        sort = "DESC";
      } else {
        sort = "ASC";
      }
      this.currentPageJump = page;
      let tempparam = {};
      tempparam.query = `?sorting=${sort}&sortby=${sortby}&pagesize=${page_size}&page=${page}`;
      tempparam.beneficiaryid = this.$CryptoJS.AES.decrypt(
        this.$route.params?.beneficiary_id,
        "beneficiary_id",
      ).toString(this.$CryptoJS.enc.Utf8);
      await this.$store
        .dispatch(GET_LINKED_REMITTERS, tempparam)
        .then((response) => {
          if (response.status == 200) {
            response.data.details.map((item) => {
              let statusobj = "";
              if (item.status === 1) {
                statusobj = { name: "Active", class: "badge-success" };
              } else if (item.status === 0) {
                statusobj = { name: "InActive", class: "badge-danger" };
              } else {
                statusobj = { name: "N/A", class: "badge-grey" };
              }
              let customerLists = {
                id: item.id,
                name: item.firstname != null ? item.firstname : "",
                mname: item.middlename != null ? item.middlename : "",
                lname: item.lastname != null ? item.lastname : "",
                receiver_fname:
                  item.beneffirstname != null ? item.beneffirstname : "",
                receiver_lname:
                  item.beneflastname != null ? item.beneflastname : "",
                payangel_id: item.payangelId ? item.payangelId : "-",
                phonenumber: item.phone ? item.phone : "-",
                country: item.countryofresidence
                  ? this.countryList.filter(
                      (data) => data.countryid === item.countryofresidence,
                    )[0]
                  : "-",
                status: statusobj,
              };
              tempitems.push(customerLists);
            });
            this.totalRows = response.data.query.total;
          }
        });
      this.linkedSenders = tempitems;
    },
    signalFor(id) {
      return this.remitterSignals[id] || null;
    },
    reasonChipClass(level) {
      if (level === "High") return "chip-red";
      if (level === "Medium") return "chip-amber";
      return "chip-green";
    },
    snapBorder(sig) {
      if (!sig) return "lr-border-grey";
      if (sig.level === "High") return "lr-border-red";
      if (sig.level === "Medium") return "lr-border-amber";
      return "lr-border-green";
    },
    // Receiver-level context (summary cards + snapshot) and per-remitter link signals.
    async fetchReceiverContext() {
      const beneficiaryid = this.$CryptoJS.AES.decrypt(
        this.$route.params?.beneficiary_id,
        "beneficiary_id",
      ).toString(this.$CryptoJS.enc.Utf8);
      if (!beneficiaryid) return;
      const [sigRes, evRes] = await Promise.all([
        this.$store.dispatch(GET_RECEIVER_REMITTER_SIGNALS, beneficiaryid),
        this.$store.dispatch(GET_RECEIVER_RISK_EVIDENCE, beneficiaryid),
      ]);
      if (sigRes && sigRes.status == 200 && sigRes.data.details) {
        this.remitterSignals = sigRes.data.details;
      }
      if (evRes && evRes.status == 200 && evRes.data.details) {
        this.receiverEvidence = evRes.data.details;
      }
    },
  },
  async mounted() {
    await this.linkedRemitter();
    this.getBankAccounts();
    this.getmomoAccounts();
    this.fetchReceiverContext();
  },
  watch: {
    currentPage: {
      handler: function () {
        this.linkedRemitter();
      },
    },
    perPage: {
      handler: function () {
        this.linkedRemitter();
      },
    },
  },
  computed: {
    ...mapGetters(["isLoading", "countryList"]),
    hasBlockPermission() {
      return this.$store.getters["authpermission/hasAnyPermission"]([
        "receiver.block",
      ]);
    },
    bankColumns() {
      return this.hasBlockPermission
        ? this.bankfields
        : this.bankfields.filter((f) => f.key !== "block_action");
    },
    momoColumns() {
      return this.hasBlockPermission
        ? this.momoaccountfields
        : this.momoaccountfields.filter((f) => f.key !== "block_action");
    },
    receiverName() {
      const r =
        Array.isArray(this.linkedSenders) && this.linkedSenders.length
          ? this.linkedSenders[0]
          : null;
      return r ? [r.receiver_fname, r.receiver_lname].filter(Boolean).join(" ") : "";
    },
  },
};
</script>

<style scoped>
.lr-count-pill {
  background: #eef2f5;
  color: #2b6cb0;
  font-size: 12px;
  font-weight: 600;
  padding: 6px 12px;
  border-radius: 16px;
  white-space: nowrap;
}

/* Readable risk pills (Link Signal column). */
.risk-pill {
  border: 1px solid;
  border-radius: 14px;
  padding: 2px 10px;
  font-size: 11px;
  font-weight: 700;
}
.risk-pill.risk-low {
  color: #256a37;
  border-color: #9ccaa9;
  background: #eef8f1;
}
.risk-pill.risk-medium {
  color: #8a6d00;
  border-color: #e2c15a;
  background: #fdf6e3;
}
.risk-pill.risk-high {
  color: #a72727;
  border-color: #e2a1a1;
  background: #fdeeee;
}
.lr-reasons {
  display: flex;
  flex-direction: column;
  gap: 4px;
}
.lr-reason-chip {
  display: inline-block;
  border: 1px solid;
  border-radius: 5px;
  padding: 2px 8px;
  font-size: 11px;
  font-weight: 600;
  width: fit-content;
}
.chip-red {
  color: #a72727;
  border-color: #e2a1a1;
  background: #fdeeee;
}
.chip-amber {
  color: #8a6d00;
  border-color: #e2c15a;
  background: #fdf6e3;
}
.chip-green {
  color: #256a37;
  border-color: #9ccaa9;
  background: #eef8f1;
}

/* Summary metric cards. */
.lr-metric {
  background: #fff;
  border: 1px solid #e6ebef;
  border-left-width: 4px;
  border-radius: 6px;
  padding: 14px 16px;
  height: 100%;
}
.lr-metric-label {
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.04em;
  color: #8a97a3;
}
.lr-metric-value {
  font-size: 28px;
  font-weight: 700;
  line-height: 1.1;
  margin: 2px 0;
}
.lr-metric-sub {
  font-size: 12px;
  color: #6b7782;
}

/* Snapshot cards. */
.lr-snapshot-head {
  background: #f4f7f9;
}
.lr-snap-title {
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.04em;
  color: #8a97a3;
  margin-bottom: 8px;
}
.lr-snap-card {
  background: #fff;
  border: 1px solid #e6ebef;
  border-left-width: 4px;
  border-radius: 6px;
  padding: 8px 12px;
  margin-bottom: 8px;
}
.lr-snap-name {
  font-weight: 700;
  font-size: 13px;
}
.lr-snap-sub {
  font-size: 12px;
  color: #6b7782;
}

/* Left-border accents (shared by metric + snapshot cards). */
.lr-border-low,
.lr-border-green {
  border-left-color: #4b9460;
}
.lr-border-medium,
.lr-border-amber {
  border-left-color: #d6a419;
}
.lr-border-high,
.lr-border-red {
  border-left-color: #c0392b;
}
.lr-border-blue {
  border-left-color: #2b6cb0;
}
.lr-border-grey {
  border-left-color: #b8c2cb;
}
</style>
